import { SlideFrame } from '../SlideFrame';
import { HoldingsList } from '@/components/research/HoldingsList';
import type { ScorecardRow, PortfolioAvg } from '../types';
import { secondsAgo } from '../utils';

// Slide 14 now embeds the same Holdings table that lives on /portfolio.
// Identical column layout, identical green tint on names currently in the
// Alpha Zone basket (αDays > 0). Steve directive 2026-05-14: "i want the
// second one instead of this one" — replaced the simplified factor-only
// scorecard with the full /portfolio HoldingsList view so the deck
// audience sees the actual book the same way the PM sees it.
//
// readOnly hides the edit-weights + update-prices + add-position controls.
// alphaSymbols (legacy prop) is kept on the type signature so callers can
// still pass it, but the green highlight now flows through HoldingsRow via
// alphaDaysBySymbol natively — no need to recompute here.
interface Props {
  rows: ScorecardRow[];
  cashPct: number;
  portfolioAvg: PortfolioAvg;
  universe: number;
  scorecardUpdatedAt: Date | null;
  scorecardRefreshing: boolean;
  alphaSymbols?: Set<string>;
}

export function Slide14Scorecard({ rows, universe, scorecardUpdatedAt, scorecardRefreshing, alphaSymbols }: Props) {
  const overlap = alphaSymbols ? rows.filter(r => alphaSymbols.has(r.symbol)).length : 0;
  return (
    <SlideFrame pageNum={18} title="OUR PORTFOLIO  —  SCORED"
      banner={overlap > 0
        ? `Higher quality at the same valuation as the market. ${overlap} of our holdings are also in today's Alpha Zone basket.`
        : 'Higher quality at the same valuation as the market.'}>
      <div className="h-full flex flex-col gap-1 pt-1 pb-1" style={{ marginTop: '-19px' }}>
        <div className="flex items-center justify-between px-1">
          <div className="text-[#94a3b8] text-[10px] flex items-center gap-2">
            <span className={`inline-block w-1.5 h-1.5 rounded-full ${scorecardRefreshing ? 'bg-amber-400' : 'bg-emerald-400'} ${scorecardRefreshing ? 'animate-pulse' : ''}`} />
            <span>{scorecardRefreshing ? 'Refreshing prices…' : (scorecardUpdatedAt ? `Live · ${scorecardUpdatedAt.toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' })} · last update ${secondsAgo(scorecardUpdatedAt)}s ago` : 'Live')}</span>
          </div>
          <p className="text-[#94a3b8] text-[10px]">
            Each holding scored against {universe} stocks. Same view as <span className="text-white">/portfolio</span> in the application.
          </p>
        </div>
        {/* Scaled down so all rows including the PORTFOLIO totals row paint
            inside the slide. PRIOR BUG (2026-05-15): scale(0.88) shrinks
            visually but DOM height stays full-size, so `overflow-hidden` clipped
            the bottom ~12% — exactly where the totals row sits. Steve flagged
            this 10× before we caught it. Fix: drop overflow-hidden, scale to
            0.78 so the visual height naturally fits the remaining slide space. */}
        <div className="flex-1">
          <div style={{ transform: 'scale(0.78)', transformOrigin: 'top left', width: 'calc(100% / 0.78)' }}>
            <HoldingsList readOnly />
          </div>
        </div>
      </div>
    </SlideFrame>
  );
}
